import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
  deletePiece,
  getPieces,
  editPiece,
  getHomePieceId,
  setHomePieceId,
  getPieceByID,
  getPieceSubscriptionByID,
  addPieceSubscription,
  deletePieceSubscription,
  getPiecesAddOns
} from '../../store/actions/piece/piece.action';
import { getSubscriptions } from '../../store/actions/subscription/subscription.action';
import PiecesList from '../../components/Pieces/Pieces';
import PageLoader from '../../components/Common/PageLoader';

const PiecesContainer = ({ value }) => {
  // Redux hooks
  const dispatch = useDispatch();

  // Redux state
  const pieces = useSelector((state) => state.piece.list);
  const page = useSelector((state) => state.piece.page);
  const isFetching = useSelector((state) => state.piece.isFetching);
  const isCreating = useSelector((state) => state.piece.isCreating);
  const categories = useSelector((state) => state.auth.categories);
  const homePieceId = useSelector((state) => state.piece.homePieceId);
  const subscriptions = useSelector((state) => state.subscription.list);
  const pieces_categories = useSelector((state) => state.piece.pieces_categories);

  // Local state
  const [mode, setMode] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);

  // Set access based on categories
  const setAccess = useCallback(() => {
    try {
      const pieceCategory = categories.find((category) => category.link === 'pieces');
      if (pieceCategory) {
        setMode(pieceCategory.mode);
        setAccessDenied(false);
      } else {
        throw new Error('Piece category not found');
      }
    } catch (err) {
      setAccessDenied(true);
    }
  }, [categories]);

  // Action handlers
  const handleDeletePiece = useCallback((piece) => {
    return dispatch(deletePiece(piece));
  }, [dispatch]);

  const handleEditPiece = useCallback((piece) => {
    return dispatch(editPiece(piece));
  }, [dispatch]);

  const handleGetHomePieceId = useCallback(() => {
    return dispatch(getHomePieceId());
  }, [dispatch]);

  const handleSetHomePieceId = useCallback((id) => {
    return dispatch(setHomePieceId(id));
  }, [dispatch]);

  const handleGetPieceByID = useCallback((id) => {
    return dispatch(getPieceByID(id));
  }, [dispatch]);

  const handleGetPieceSubscriptionByID = useCallback((id) => {
    return dispatch(getPieceSubscriptionByID(id));
  }, [dispatch]);

  const handleAddPieceSubscription = useCallback((data) => {
    return dispatch(addPieceSubscription(data));
  }, [dispatch]);

  const handleDeletePieceSubscription = useCallback((id) => {
    return dispatch(deletePieceSubscription(id));
  }, [dispatch]);

  const handleGetPiecesAddOns = useCallback((data) => {
    return dispatch(getPiecesAddOns(data));
  }, [dispatch]);

  // Initialize on mount
  useEffect(() => {
    dispatch(getPieces());
    dispatch(getSubscriptions());
    setAccess();
  }, [dispatch, setAccess]);

  // Update access when categories change
  useEffect(() => {
    setAccess();
  }, [categories, setAccess]);

  // Loading state
  if (isFetching) {
    return <PageLoader />;
  }

  // Access denied state
  if (accessDenied) {
    return <div>This category is not accessible to the logged in user.</div>;
  }

  // Prepare props for PiecesList
  const pieceInfo = {
    deletePiece: handleDeletePiece,
    pieces,
    getPieceByID: handleGetPieceByID
  };

  const homePieceMethods = {
    getHomePieceId: handleGetHomePieceId,
    setHomePieceId: handleSetHomePieceId,
    homePieceId
  };

  const pieceSubscriptionMethods = {
    getPieceSubscriptionByID: handleGetPieceSubscriptionByID,
    addPieceSubscription: handleAddPieceSubscription,
    deletePieceSubscription: handleDeletePieceSubscription
  };

  return (
    <PiecesList
      homePieceMethods={homePieceMethods}
      pieceInfo={pieceInfo}
      editPiece={handleEditPiece}
      value={value}
      mode={mode}
      piece_page={page}
      subscriptions={subscriptions}
      pieceSubscriptionMethods={pieceSubscriptionMethods}
      getPiecesAddOns={handleGetPiecesAddOns}
      pieces_categories={pieces_categories}
    />
  );
};

export default PiecesContainer;
